import { HorizontalEndIcon } from "components/common/Icons/Alignment/HorizontalEndIcon";
import { HorizontalMiddleIcon } from "components/common/Icons/Alignment/HorizontalMiddleIcon";
import { HorizontalStartIcon } from "components/common/Icons/Alignment/HorizontalStartIcon";
import { VerticalMiddleIcon } from "components/common/Icons/Alignment/VerticalMiddleIcon";
import { VerticalStartIcon } from "components/common/Icons/Alignment/VerticalStartIcon";
import { Translate } from "components/common/Translate/Translate";
import { ReactNode } from "react";
import { setSettings } from "store/features/memeGenerator/actions";
import { TextBox } from "store/features/memeGenerator/types";
import { useAppDispatch } from "store/store";

export interface AlignmentOptionsProps {
    textBox: TextBox;
}

export const AlignmentOptions = ({ textBox }: AlignmentOptionsProps) => {
    const dispatch = useAppDispatch();
    return (
        <div
            className="absolute top-full left-0 translate-y-2 bg-white dark:bg-darkSurface-200 border-[0.5px] border-[#DCDDDF] dark:border-darkSurface-100 rounded-xl p-5 flex flex-col gap-[14px] text-black dark:text-white"
            style={{
                boxShadow: `0px 4px 50px rgba(0, 0, 0, 0.08)`,
            }}
        >
            <div className="whitespace-nowrap">
                <Translate keyName="text-alignment" />
            </div>
            {/* horizontal options */}
            <div className="flex items-center gap-5">
                <Chip
                    icon={
                        <HorizontalStartIcon
                            className={`${
                                textBox.alignment.horizontal === "start" ? "fill-white" : "fill-black dark:fill-white"
                            } group-hover:fill-white`}
                        />
                    }
                    isActive={textBox.alignment.horizontal === "start"}
                    onClick={() =>
                        dispatch(
                            setSettings({
                                id: textBox.id,
                                alignment: {
                                    ...textBox.alignment,
                                    horizontal: "start",
                                },
                            }),
                        )
                    }
                />
                <Chip
                    icon={
                        <HorizontalMiddleIcon
                            className={`${
                                textBox.alignment.horizontal === "middle" ? "fill-white" : "fill-black dark:fill-white"
                            } group-hover:fill-white`}
                        />
                    }
                    isActive={textBox.alignment.horizontal === "middle"}
                    onClick={() =>
                        dispatch(
                            setSettings({
                                id: textBox.id,
                                alignment: {
                                    ...textBox.alignment,
                                    horizontal: "middle",
                                },
                            }),
                        )
                    }
                />
                <Chip
                    icon={
                        <HorizontalEndIcon
                            className={`${
                                textBox.alignment.horizontal === "end" ? "fill-white" : "fill-black dark:fill-white"
                            } group-hover:fill-white`}
                        />
                    }
                    isActive={textBox.alignment.horizontal === "end"}
                    onClick={() =>
                        dispatch(
                            setSettings({
                                id: textBox.id,
                                alignment: {
                                    ...textBox.alignment,
                                    horizontal: "end",
                                },
                            }),
                        )
                    }
                />
            </div>
            <div className="flex items-center gap-5">
                <Chip
                    icon={
                        <VerticalStartIcon
                            className={`${
                                textBox.alignment.vertical === "start" ? "fill-white" : "fill-black dark:fill-white"
                            } group-hover:fill-white`}
                        />
                    }
                    isActive={textBox.alignment.vertical === "start"}
                    onClick={() =>
                        dispatch(
                            setSettings({
                                id: textBox.id,
                                alignment: {
                                    ...textBox.alignment,
                                    vertical: "start",
                                },
                            }),
                        )
                    }
                />
                <Chip
                    icon={
                        <VerticalMiddleIcon
                            className={`${
                                textBox.alignment.vertical === "middle" ? "fill-white" : "fill-black dark:fill-white"
                            } group-hover:fill-white`}
                        />
                    }
                    isActive={textBox.alignment.vertical === "middle"}
                    onClick={() =>
                        dispatch(
                            setSettings({
                                id: textBox.id,
                                alignment: {
                                    ...textBox.alignment,
                                    vertical: "middle",
                                },
                            }),
                        )
                    }
                />
                <Chip
                    icon={
                        <div className="rotate-180">
                            <VerticalStartIcon
                                className={`${
                                    textBox.alignment.vertical === "end" ? "fill-white" : "fill-black dark:fill-white"
                                } group-hover:fill-white`}
                            />
                        </div>
                    }
                    isActive={textBox.alignment.vertical === "end"}
                    onClick={() =>
                        dispatch(
                            setSettings({
                                id: textBox.id,
                                alignment: {
                                    ...textBox.alignment,
                                    vertical: "end",
                                },
                            }),
                        )
                    }
                />
            </div>
        </div>
    );
};

interface ChipProps {
    icon: ReactNode;
    isActive: boolean;
    onClick: () => void;
}

const Chip = ({ icon, isActive, onClick }: ChipProps) => {
    return (
        <button
            className={`group w-10 h-10 rounded-xl border border-[#eeeeee] dark:border-darkSurface-400 flex items-center justify-center hover:bg-irBlue-200 ${
                isActive ? "bg-irBlue-200 dark:bg-darkPrimary-100" : ""
            }`}
            onClick={onClick}
        >
            {icon}
        </button>
    );
};
